import React, { useRef, useEffect } from 'react';
import './StepIndicator.css';

interface Step {
    number: number;
    title: string;
    status: 'completed' | 'current' | 'pending';
}

interface StepIndicatorProps {
    steps: Step[];
    currentStep: number;
    onStepClick?: (step: number) => void;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStep, onStepClick }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const activeStepRef = useRef<HTMLDivElement>(null);

    // Hacer scroll al paso actual cuando cambia o cuando se monta el componente
    useEffect(() => {
        if (activeStepRef.current && containerRef.current) {
            // Pequeño delay para asegurar que el DOM está renderizado
            setTimeout(() => {
                activeStepRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }, 100);
        }
    }, [currentStep]);

    const handleStepClick = (step: Step) => {
        if (onStepClick && (step.status === 'completed' || step.status === 'current')) {
            onStepClick(step.number);
        }
    };

    const getStepClass = (step: Step) => {
        const classes = ['step-indicator-item'];
        
        if (step.number === currentStep) {
            classes.push('active');
        }
        
        if (step.status === 'completed') {
            classes.push('completed');
        } else if (step.status === 'pending') {
            classes.push('disabled');
        }
        
        return classes.join(' ');
    };

    const isClickable = (step: Step) => {
        return step.status === 'completed' || step.status === 'current';
    };

    return (
        <div className="step-indicator" ref={containerRef}>
            {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                    <div 
                        ref={step.number === currentStep ? activeStepRef : null}
                        className={getStepClass(step)}
                        onClick={() => handleStepClick(step)}
                        style={{ cursor: isClickable(step) ? 'pointer' : 'not-allowed' }}
                    >
                        <div className="step-number">
                            {step.status === 'completed' ? (
                                <i className="fas fa-check"></i>
                            ) : (
                                step.number
                            )}
                        </div>
                        <div className="">{step.title}</div>
                    </div>
                    
                    {index < steps.length - 1 && (
                        <div className={`step-connector ${step.status === 'completed' ? 'completed' : ''}`}></div>
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};

export default StepIndicator;

